import type { CaptionsDelivery } from '../api';
import { t, type Key } from '../i18n';
import { langName } from './captions';

/*
 * CAPTIONS-DELIVERY-1 (docs/captions-delivery.md): what an output row says about how its
 * platform gets the captions. The center picks the method (captions_delivery_effective:
 * method, lang, state, reason — codes); the words are made here. YouTube: HTTP; Twitch and
 * Facebook: CEA-608 in English or Spanish; everything else burned into the video, which
 * viewers cannot turn off and which counts in the plan's transcoding hours.
 */

export interface CaptionsDeliveryView {
  /** "CC · English (CEA-608)", "Captions · YouTube", "Captions burned in · Russian — uses transcoding hours" */
  label?: string;
  tone: 'live' | 'start' | 'warn' | 'muted';
  notes: { code: string; text: string; tone: 'warn' | 'muted' }[];
  method: CaptionsDelivery['method'];
  state: CaptionsDelivery['state'];
}

/** platforms where 608 exists but carries Latin script only: other languages are burned in there */
const CC608_PLATFORMS: ReadonlySet<string> = new Set(['twitch', 'facebook']);

/** a language name in the cabinet's language (EN/ES/RU), else the English name */
export function ccLang(code?: string): string {
  if (!code) return '';
  const key = `cc.lang.${code}` as Key;
  const s = t(key);
  return s === key || s === undefined ? langName(code) : s;
}

/** undefined: nothing to say (the stream's captions are off). */
export function captionsDeliveryView(
  eff: CaptionsDelivery | undefined,
  platform: string,
  profile: string,
): CaptionsDeliveryView | undefined {
  if (!eff) return undefined;
  if (eff.state === 'off' && eff.reason !== 'user_off') return undefined;
  const p = platform;
  const lang = ccLang(eff.lang);
  const notes: CaptionsDeliveryView['notes'] = [];
  const v = (label: string | undefined, tone: CaptionsDeliveryView['tone']): CaptionsDeliveryView => ({ label, tone, notes, method: eff.method, state: eff.state });

  let label: string | undefined;
  switch (eff.method) {
    case 'http':
      label = eff.translation ? t('cd.httpLang', { platform: p, lang }) : t('cd.http', { platform: p });
      break;
    case 'cea608':
      label = t('cd.cc608', { lang });
      break;
    case 'burn':
      label = lang ? t('cd.burn', { lang }) : t('cd.burnOriginal');
      notes.push({ code: 'burn_fixed', tone: 'muted', text: t('cd.burnFixed') });
      if (CC608_PLATFORMS.has(profile) && eff.lang && eff.lang !== 'en' && eff.lang !== 'es') {
        notes.push({ code: 'burn_608', tone: 'muted', text: t('cd.burn608', { platform: p }) });
      }
      break;
    case 'off':
      return v(t('cd.off'), 'muted');
  }

  const warn = (code: string, key: Key) => notes.unshift({ code, tone: 'warn', text: t(key, { platform: p, lang }) });
  switch (eff.state) {
    case 'on':
      return v(label, 'live');
    case 'waiting_captions':
      return v(t('cd.waiting'), 'start');
    case 'node_unsupported':
      warn('node_unsupported', 'cd.nodeNo608');
      return v(label, 'warn');
    case 'plan':
      warn('plan', eff.reason === 'plan_no_caption_translation' ? 'cd.planTranslate' : 'cd.planNone');
      return v(label, 'warn');
    case 'unavailable': {
      if (eff.reason === 'http_not_set_up') {
        notes.unshift({ code: 'http_not_set_up', tone: 'muted', text: t('cd.httpOff', { platform: p }) });
        return v(label, 'muted');
      }
      const key: Key =
        eff.reason === 'burn_not_ready' ? 'cd.burnNotReady'
        : eff.reason === 'no_transcoder' ? 'cd.noTranscoder'
        : eff.reason === 'plan_limit_transcode_hours' ? 'cd.hoursOut'
        : eff.reason === 'cc608_not_ready' ? 'cd.cc608NotReady'
        : eff.reason === 'translation_not_selected' ? 'cd.notTranslated'
        : 'cd.unavailable';
      warn(eff.reason ?? 'unavailable', key);
      return v(label, 'warn');
    }
  }
  return v(label, 'muted');
}
